import 'bootstrap/dist/css/bootstrap.min.css'
import 'bootstrap/dist/js/bootstrap.min.js'
import '@fontsource/roboto/100.css'
import '@fontsource/roboto/300.css'
import '@fontsource/roboto/400.css'
import '@fontsource/roboto/500.css'
import '@fontsource/roboto/700.css'
import '@fontsource/roboto/900.css'

import {
  type GatsbyBrowser,
  type ShouldUpdateScrollArgs,
  type WrapRootElementBrowserArgs
} from 'gatsby'
import React from 'react'

import RootElement from '@/components/root-element'
import { buildUTMTrackingUrl } from '@/utils/analytics'

export const wrapRootElement: GatsbyBrowser['wrapRootElement'] = ({
  element
}: WrapRootElementBrowserArgs) => {
  return <RootElement>{element}</RootElement>
}

export const shouldUpdateScroll: GatsbyBrowser['shouldUpdateScroll'] = ({
  routerProps: { location },
  prevRouterProps
}: ShouldUpdateScrollArgs) => {
  /* Checking if the pathname and search are the same, if they are, then it returns false to avoid scrolling top */
  if (
    location &&
    prevRouterProps?.location &&
    location?.pathname === prevRouterProps?.location?.pathname &&
    location?.search !== prevRouterProps.location?.search
  ) {
    return false
  }

  // Handle anchor navigation within the same page
  if (
    location &&
    prevRouterProps?.location &&
    location?.pathname === prevRouterProps?.location?.pathname &&
    location?.search === prevRouterProps.location?.search &&
    location?.hash !== prevRouterProps.location?.hash
  ) {
    // Let our custom useAnchorScrollFix hook handle the scroll positioning
    // This ensures consistent behavior between direct access and navigation
    return false
  }

  // Handle direct access to pages with anchors (initial page load)
  if (location?.hash && !prevRouterProps?.location) {
    // Let our custom useAnchorScrollFix hook handle initial scroll positioning
    return false
  }

  return true
}

let first = true

function getDuration() {
  const start = window.start || new Date()
  const now = new Date()
  const difference = now.getTime() - start.getTime()

  if (difference === 0) {
    return null
  }

  return difference
}

export const onRouteUpdate: GatsbyBrowser['onRouteUpdate'] = ({
  location,
  prevLocation
}) => {
  if (process.env.NODE_ENV === 'production' || window.dev === true) {
    if (!window._paq) return

    const { _paq, dev } = window
    const url = location && location.pathname + location.search + location.hash
    const prevUrl =
      prevLocation &&
      prevLocation.pathname + prevLocation.search + prevLocation.hash

    // send UTM parameters to Matomo if they exist on the URL or sessionStorage
    const trackingUrl = buildUTMTrackingUrl(url)
    const trackLoad = false

    // document.title workaround stolen from:
    // https://github.com/gatsbyjs/gatsby/blob/master/packages/gatsby-plugin-google-analytics/src/gatsby-browser.js
    const sendPageView = () => {
      const { title } = document

      if (prevUrl) {
        _paq.push(['setReferrerUrl', prevUrl])
      }
      _paq.push(['setCustomUrl', trackingUrl])
      _paq.push(['setDocumentTitle', title])
      _paq.push(['trackPageView'])
      _paq.push(['enableLinkTracking'])
      _paq.push(['trackAllContentImpressions'])

      if (dev) {
        console.debug(`[Matomo] Page view for: ${trackingUrl} - ${title}`)
      }
    }

    // Minimum delay for reactHelmet's requestAnimationFrame
    const delay = Math.max(32, 0)
    setTimeout(sendPageView, delay)

    if (first) {
      first = false

      if (trackLoad) {
        _paq.push([
          'trackEvent',
          'javascript',
          'load',
          'duration',
          getDuration()
        ])
      }

      if (dev) {
        console.debug(`[Matomo] Tracking duration for: ${trackingUrl}`)
      }
    }
  }

  return null
}
